import { useEffect, useRef } from 'react'
import { GoogleTagManager, sendGTMEvent } from '@next/third-parties/google'
import {
  ComponentPropsContext,
  Field,
  Placeholder,
  SitecoreContext,
  StaticPath,
} from '@sitecore-jss/sitecore-jss-nextjs'
import { GetStaticPaths, GetStaticProps } from 'next'
import { useRouter } from 'next/router'

import ADFJobDetails from 'components/ADFJobDetails'
import { PageStateProvider } from 'components/front-end/_layoutContext'
import SharedContextProvider from 'components/front-end/_sharedContext'
import { NavigationProvider } from 'components/front-end/Header/NavigationProvider'
import { JobData } from 'components/front-end/JobDetails/_JobInterface'
import { ShortlistProvider } from 'components/front-end/Shortlist/ShortlistProvider'
import ADFMetadata from 'components/front-end/SiteMetadata/Metadata'
import { TwIndicator } from 'components/front-end/Utility/TwIndicator'
import { SitecorePageProps } from 'lib/page-props'
import { sitecorePagePropsFactory } from 'lib/page-props-factory'
import { trackPageLoad } from 'src/utils/tracking'
import { componentFactory } from 'temp/componentFactory'
import Scripts from 'src/Scripts'

interface JobPageProps extends SitecorePageProps {
  jobData: JobData
}

interface RouteFields {
  [key: string]: unknown
  Title?: Field
}

export const generateClasses = (fields: RouteFields | undefined) => {
  const themes = ['light', 'dark']
  const service = (fields?.['Service'] as Field<string>)?.value
  const theme = (fields?.['Theme'] as Field<string>)?.value ?? themes[0]
  const bg =
    theme === themes[0]
      ? ['bg-white', 'text-grey-medium', 'font-inter']
      : ['dark', theme === 'grey' ? 'bg-black' : 'bg-black-off', 'text-white', 'font-inter']

  const bodyClasses = []
  if (theme) {
    bodyClasses.push(theme)
  }
  if (service) {
    bodyClasses.push(service)
  }
  if (bg) {
    bodyClasses.push(...bg)
  }
  if (theme === 'grey') {
    bodyClasses.push('[&_main]:mt-[-65px]', 'md:[&_main]:-mt-22')
  }
  return bodyClasses
}

const JobPage = ({ componentProps, layoutData, jobData }: JobPageProps): JSX.Element => {
  const route = layoutData?.sitecore?.route ?? null
  const isEditing = layoutData?.sitecore?.context.pageEditing ?? false
  const fields = route?.fields as RouteFields

  const router = useRouter()

  // layout useEffect
  useEffect(() => {
    // Sitecore has capital names for Theme and Service
    const body = window.document?.body
    const bodyClasses = generateClasses(fields)
    body.classList.add(...bodyClasses)

    return () => {
      body.classList.remove(...bodyClasses)
    }
  }, [route, fields])

  const previousRoute = useRef('')

  useEffect(() => {
    const handleRouteChange = () => {
      // Prevent duplicate events
      if (previousRoute.current === router.asPath) {
        return
      }

      trackPageLoad()

      if (previousRoute.current) {
        // Simulate a `js`, `dom`, and `load` events so GTM can track client-side navigation
        sendGTMEvent({ event: 'gtm.js' })
        sendGTMEvent({ event: 'gtm.dom' })
        sendGTMEvent({ event: 'gtm.load' })
      }

      previousRoute.current = router.asPath
    }

    // Fire on initial page load
    handleRouteChange()

    router.events.on('routeChangeComplete', handleRouteChange)
    return () => {
      router.events.off('routeChangeComplete', handleRouteChange)
    }
  }, [router])
  // -------------------------

  return (
    <ComponentPropsContext value={componentProps}>
      <SitecoreContext componentFactory={componentFactory} layoutData={layoutData}>
        <Scripts />

        <ADFMetadata layoutData={layoutData} />

        <PageStateProvider>
          <ShortlistProvider>
            <NavigationProvider>
              <SharedContextProvider>
                <div>
                  <header className={`relative z-50`}>
                    <div id="header">{route && <Placeholder name="headless-header" rendering={route} />}</div>
                  </header>
                  <main className="min-h-[500px]">
                    <div id="content">
                      {isEditing ? (
                        <>{route && <Placeholder name="headless-main" rendering={route} />}</>
                      ) : (
                        <ADFJobDetails jobData={jobData} />
                      )}
                    </div>
                  </main>
                  <footer>
                    <div id="footer">{route && <Placeholder name="headless-footer" rendering={route} />}</div>
                  </footer>
                  <div id="drawers" role="complementary">
                    {route && <Placeholder name="headless-drawers" rendering={route} />}
                  </div>
                </div>
              </SharedContextProvider>
            </NavigationProvider>
          </ShortlistProvider>
        </PageStateProvider>
        <GoogleTagManager gtmId={process.env.NEXT_PUBLIC_GA_ID as string} />
        <TwIndicator />
      </SitecoreContext>
    </ComponentPropsContext>
  )
}

// This function gets called at build and export time to determine
// pages for SSG ("paths", as tokenized array).
export const getStaticPaths: GetStaticPaths = async () => {
  // Fallback, along with revalidate in getStaticProps (below),
  // enables Incremental Static Regeneration. This allows us to
  // leave certain (or all) paths empty if desired and static pages
  // will be generated on request (development mode in this example).
  // Alternatively, the entire sitemap could be pre-rendered
  // ahead of time (non-development mode in this example).
  // See https://nextjs.org/docs/basic-features/data-fetching/incremental-static-regeneration

  const paths: StaticPath[] = []
  const fallback: boolean | 'blocking' = 'blocking'

  return {
    paths,
    fallback,
  }
}

// This function gets called at build time on server-side.
// It may be called again, on a serverless function, if
// revalidation (or fallback) is enabled and a new request comes in.
export const getStaticProps: GetStaticProps = async (context) => {
  const path1 = context.params?.path?.[0]
  const path2 = context.params?.path?.[1]
  const path3 = context.params?.path?.[2]

  const isReserve = path1 == 'reserves'
  const service = isReserve ? path2 : path1
  const job = isReserve ? path3 : path2

  try {
    const responseAsync = fetch(`${process.env.SITECORE_API_HOST}/api/v2/jobs/JobDetailsSearch`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ name: job, service, isReserve }),
    })

    context.params = { path: ['jobs', 'job-details'] }

    const propsAsync = sitecorePagePropsFactory.create(context) as Promise<JobPageProps>

    const [response, props] = await Promise.all([responseAsync, propsAsync])
    if (response.status === 200) {
      props.jobData = await response.json()
    }

    return {
      props,
      // Next.js will attempt to re-generate the page:
      // - When a request comes in
      // - At most once every 5 seconds
      revalidate: 5, // In seconds
      notFound: response.status !== 200,
    }
  } catch (error) {
    console.error(error)
  }
  return {
    props: {},
    // Next.js will attempt to re-generate the page:
    // - When a request comes in
    // - At most once every 5 seconds
    revalidate: 1, // In seconds
    notFound: true,
  }
}

export default JobPage
